import Axios from 'axios';
import { APP_SECRET_KEY } from '../constants';
import CryptoJS from 'crypto-js';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://staging.cvgcoops.com.ng/api/v1';

export const baseUrlNoAuth = Axios.create({
  baseURL: 'https://staging.cvgcoops.com.ng/api/v1/',
  headers: { 'Content-Type': 'application/json' },
});

export const baseUrlWithAuth = Axios.create({
  baseURL: 'https://staging.cvgcoops.com.ng/api/v1/',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const virtualAccountBaseUrl = Axios.create({
  baseURL: 'https://zippyworld.net/zippy_event_third_party/v1/api',
  headers: {
    'x-api-key': '6128892',
    'Content-Type': 'application/json',
  },
});

const isDevelopment = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.endsWith('.local')
  );
};

const getPartnerSubdomain = (forcedSubdomain = null) => {
  if (forcedSubdomain) {
    return forcedSubdomain;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const partnerParam = urlParams.get('partner') || urlParams.get('subdomain');
  if (partnerParam) {
    return partnerParam;
  }

  if (isDevelopment()) {
    const savedPartner = localStorage.getItem('dev_partner_subdomain');
    if (savedPartner) {
      return savedPartner;
    }
  }

  const hostname = window.location.hostname;
  if (
    hostname.includes('.cloudvantage.com.ng') &&
    hostname.includes('.staging') &&
    hostname !== 'cloudvantage.com.ng' &&
    hostname !== 'staging.cloudvantage.com.ng'
  ) {
    return hostname.split('.')[0];
  }

  return null;
};

const addPartnerDomainParam = (url, subdomain) => {
  if (!subdomain) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}partner_domain=${subdomain}`;
};

const createPartnerAxiosInstance = (baseURL) => {
  const instance = Axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const extractBearerToken = (session) => {
    if (!session) return null;
    // Support a few common response shapes/keys
    return (
      session.access ||
      session.access_token ||
      session.token ||
      session.jwt ||
      session?.data?.access ||
      session?.data?.access_token ||
      session?.data?.token ||
      null
    );
  };

  const shouldForceLogout = (error) => {
    const status = error?.response?.status;
    if (status === 401) return true;

    // Some backends return 403 for invalid/expired tokens.
    // Only force logout when the payload clearly indicates auth invalidation.
    if (status === 403) {
      const msg =
        error?.response?.data?.statusmessage ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        '';
      return typeof msg === 'string' && msg.toLowerCase().includes('invalid token');
    }

    return false;
  };

  instance.interceptors.request.use(
    (config) => {
      try {
        const decryptedData = CryptoJS.AES.decrypt(
          localStorage.getItem('userSessionData'),
          APP_SECRET_KEY,
        ).toString(CryptoJS.enc.Utf8);
        const parsedData = JSON.parse(decryptedData);
        const accessKey = extractBearerToken(parsedData);
        if (accessKey) {
          config.headers['Authorization'] = `Bearer ${accessKey}`;
        }

        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        const subdomain = getPartnerSubdomain();
        if (subdomain) {
          config.url = addPartnerDomainParam(config.url, subdomain);
        }
      } catch (error) {
        console.error('Error in request interceptor:', error);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (shouldForceLogout(error)) {
        localStorage.removeItem('userSessionData');
        window.location.href = '/';
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export const getPartnerAxiosInstance = (subdomain = null) => {
  const detectedSubdomain = getPartnerSubdomain(subdomain);

  if (detectedSubdomain) {
    // const partnerBaseURL = `https://${subdomain}.cvgcoops.com.ng/api/v1`;
    const partnerBaseURL = `https://staging.cvgcoops.com.ng/api/v1`;
    return createPartnerAxiosInstance(partnerBaseURL);
  }

  return baseUrlWithAuth;
};

export const setDevelopmentPartner = (subdomain) => {
  if (isDevelopment()) {
    if (subdomain) {
      localStorage.setItem('dev_partner_subdomain', subdomain);
      console.log(`Development partner set to: ${subdomain}`);
    } else {
      localStorage.removeItem('dev_partner_subdomain');
      console.log('Development partner cleared');
    }
  } else {
    console.warn('setDevelopmentPartner only works in development environment');
  }
};

export const debugPartnerConfig = () => {
  const subdomain = getPartnerSubdomain();
  const config = {
    isDevelopment: isDevelopment(),
    hostname: window.location.hostname,
    urlParams: Object.fromEntries(new URLSearchParams(window.location.search)),
    detectedSubdomain: subdomain,
    savedDevPartner: localStorage.getItem('dev_partner_subdomain'),
    willUsePartnerAPI: !!subdomain,
  };

  console.table(config);
  return config;
};

baseUrlNoAuth.interceptors.request.use(
  (config) => {
    try {
      const subdomain = getPartnerSubdomain();
      if (
        subdomain &&
        (window.location.search.includes('partner=') ||
          window.location.search.includes('subdomain=') ||
          (isDevelopment() && localStorage.getItem('dev_partner_subdomain')))
      ) {
        config.url = addPartnerDomainParam(config.url, subdomain);
      }
    } catch (error) {
      console.error('Error in baseUrlNoAuth request interceptor:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

baseUrlWithAuth.interceptors.request.use(
  (config) => {
    try {
      const decryptedData = CryptoJS.AES.decrypt(
        localStorage.getItem('userSessionData'),
        APP_SECRET_KEY,
      ).toString(CryptoJS.enc.Utf8);
      const parsedData = JSON.parse(decryptedData);
      const accessKey =
        parsedData?.access ||
        parsedData?.access_token ||
        parsedData?.token ||
        parsedData?.jwt ||
        parsedData?.data?.access ||
        parsedData?.data?.access_token ||
        parsedData?.data?.token ||
        null;
      if (accessKey) {
        config.headers['Authorization'] = `Bearer ${accessKey}`;
      }

      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
        console.log(
          'FormData detected - Content-Type header removed for proper multipart upload',
        );
      }

      const subdomain = getPartnerSubdomain();
      if (
        subdomain &&
        (window.location.search.includes('partner=') ||
          window.location.search.includes('subdomain=') ||
          (isDevelopment() && localStorage.getItem('dev_partner_subdomain')))
      ) {
        config.url = addPartnerDomainParam(config.url, subdomain);
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

baseUrlWithAuth.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only bounce user out when they are truly unauthenticated.
    // 403 can also mean "authenticated but forbidden" and should not clear session.
    const status = error?.response?.status;
    const msg =
      error?.response?.data?.statusmessage ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      '';
    const shouldLogout =
      status === 401 ||
      (status === 403 &&
        typeof msg === 'string' &&
        msg.toLowerCase().includes('invalid token'));

    if (shouldLogout) {
      localStorage.removeItem('userSessionData');
      window.location.href = '/';
    }
    return Promise.reject(error);
  },
);
